/**
 * Bilingual SEO primitive — Spanish (es-US) variant.
 *
 * Used on every /es/* page. Owns the entire Spanish SEO surface: <html lang>,
 * <title>, canonical, hreflang loop, og:locale, og:url, robots, JSON-LD.
 *
 * IMPLEMENTATION NOTE — why imperative DOM mutation instead of react-helmet-async:
 * The static /public/index.html ships with a baked-in <meta property="og:locale"
 * content="en_US">, a full English JSON-LD @graph, and a WebPage schema. These
 * tags exist for non-JS crawlers and we cannot remove them without regressing
 * English SEO. react-helmet-async 2.0.5 in this codebase is also inconsistent
 * about which children win when multiple <Helmet> instances mount and when
 * static tags pre-exist in index.html.
 *
 * Solution: own the head imperatively for ES routes only. We mark every tag
 * we append with a data-fwd-es attribute so cleanup on unmount is exact and
 * we never touch English tags.
 */
import { useEffect } from 'react';

const SITE = 'https://fortworthdental.com';
const MARK = 'data-fwd-es';

/**
 * Replace OR append a <meta>/<link>/<script> tag in <head>. Existing tags that
 * match `selector` are removed first (so we override static index.html tags).
 * The freshly-appended tag is marked with `data-fwd-es` for cleanup.
 */
function upsertHeadTag(tagName, attrs, selector) {
  if (typeof document === 'undefined') return null;
  const head = document.head;

  // Remove ALL existing tags matching the selector (static + previous EsSEO mounts)
  if (selector) {
    head.querySelectorAll(selector).forEach((el) => el.remove());
  }

  const el = document.createElement(tagName);
  Object.entries(attrs).forEach(([k, v]) => {
    if (v === undefined || v === null || v === false) return;
    el.setAttribute(k, String(v));
  });
  el.setAttribute(MARK, '');
  head.appendChild(el);
  return el;
}

const EsSEO = ({
  title,
  description,
  canonical,        // e.g., "/es/contacto" — we prefix the domain
  hreflangEn,       // e.g., "/contact-us"
  ogImage,
  schema,           // optional JSON-LD object (will be wrapped in <script>)
  noindex = false,
}) => {
  const canonicalUrl = canonical?.startsWith('http') ? canonical : `${SITE}${canonical || ''}`;
  const enUrl = hreflangEn?.startsWith('http') ? hreflangEn : `${SITE}${hreflangEn || ''}`;

  useEffect(() => {
    if (typeof document === 'undefined') return;

    // 1. <html lang="es">
    const prevLang = document.documentElement.getAttribute('lang') || 'en';
    document.documentElement.setAttribute('lang', 'es');

    // 2. <title>
    const prevTitle = document.title;
    if (title) document.title = title;

    // 3. <meta name="description">
    if (description) {
      upsertHeadTag('meta', { name: 'description', content: description }, 'meta[name="description"]');
    }

    // 4. <link rel="canonical">  — also override any static canonical
    upsertHeadTag('link', { rel: 'canonical', href: canonicalUrl }, 'link[rel="canonical"]');

    // 5. og:locale, og:type, og:url, og:title, og:description, og:image
    upsertHeadTag('meta', { property: 'og:type', content: 'website' }, 'meta[property="og:type"]');
    upsertHeadTag('meta', { property: 'og:locale', content: 'es_US' }, 'meta[property="og:locale"]');
    upsertHeadTag('meta', { property: 'og:locale:alternate', content: 'en_US' }, 'meta[property="og:locale:alternate"]');
    upsertHeadTag('meta', { property: 'og:url', content: canonicalUrl }, 'meta[property="og:url"]');
    if (title) {
      upsertHeadTag('meta', { property: 'og:title', content: title }, 'meta[property="og:title"]');
    }
    if (description) {
      upsertHeadTag('meta', { property: 'og:description', content: description }, 'meta[property="og:description"]');
    }
    if (ogImage) {
      upsertHeadTag('meta', { property: 'og:image', content: ogImage }, 'meta[property="og:image"]');
    }

    // 6. hreflang loop — remove ALL existing hreflang alternates first, then add ours
    document.head.querySelectorAll('link[rel="alternate"][hreflang]').forEach((el) => el.remove());
    upsertHeadTag('link', { rel: 'alternate', hreflang: 'es', href: canonicalUrl });
    upsertHeadTag('link', { rel: 'alternate', hreflang: 'es-US', href: canonicalUrl });
    if (hreflangEn) {
      upsertHeadTag('link', { rel: 'alternate', hreflang: 'en', href: enUrl });
      upsertHeadTag('link', { rel: 'alternate', hreflang: 'en-US', href: enUrl });
      upsertHeadTag('link', { rel: 'alternate', hreflang: 'x-default', href: enUrl });
    }

    // 7. robots
    upsertHeadTag(
      'meta',
      {
        name: 'robots',
        content: noindex
          ? 'noindex, nofollow'
          : 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1',
      },
      'meta[name="robots"]'
    );

    // 8. JSON-LD schema (mark with id so we can remove on unmount without
    //    nuking the static @graph or RouteSchemaInjector schemas)
    if (schema) {
      // Remove any previously-injected ES schema
      document.head.querySelectorAll(`script[${MARK}][type="application/ld+json"]`).forEach((el) => el.remove());
      const s = document.createElement('script');
      s.setAttribute('type', 'application/ld+json');
      s.setAttribute(MARK, '');
      s.textContent = JSON.stringify(schema);
      document.head.appendChild(s);
    }

    // ----- Cleanup on route change / unmount -----
    return () => {
      document.documentElement.setAttribute('lang', prevLang);
      if (title) document.title = prevTitle;
      // Remove every tag we marked
      document.head.querySelectorAll(`[${MARK}]`).forEach((el) => el.remove());
      // Note: we do NOT restore the original static index.html tags. The English
      // pages re-mount RouteSchemaInjector + their own page-level Helmets which
      // re-set canonical/og/robots correctly. The static index.html tags act as
      // crawler fallbacks for non-JS environments only.
    };
  }, [title, description, canonicalUrl, enUrl, ogImage, hreflangEn, schema, noindex]);

  return null;
};

export default EsSEO;
